<template>
  <div class="ge-detail-page">
    <Navbar />

    <div v-if="loading" class="loading">
      <el-skeleton :rows="5" animated />
    </div>

    <div v-else class="ge-detail-container">
      <el-card class="ge-info-card" shadow="always">
        <template #header>
          <div class="ge-info-header">
            <h1>{{ ge.name }}</h1>
            <el-tag type="primary" size="large">{{ ge.interest?.name }}</el-tag>
          </div>
        </template>
        <p class="description">{{ ge.description }}</p>
        <div class="meta">
          <div class="moderators">
            <strong>Modérateurs :</strong>
            <el-tag
              v-for="mod in ge.moderators"
              :key="mod.id"
              size="small"
              class="mod-tag"
            >
              <UserLink :username="mod.username" />
            </el-tag>
          </div>
        </div>
      </el-card>

      <el-tabs v-model="activeTab" type="border-card">
        <el-tab-pane label="Canal public" name="public">
          <div v-if="!isMember" class="not-member">
            <el-alert
              title="Vous devez être membre approuvé de ce groupe pour voir et envoyer des messages."
              type="warning"
              show-icon
              :closable="false"
            />
            <el-button
              v-if="!membershipStatus"
              type="primary"
              class="join-btn"
              @click="requestMembership"
            >
              Demander à rejoindre
            </el-button>
            <el-tag v-else-if="membershipStatus === 'PENDING'" type="warning">
              Demande en attente
            </el-tag>
            <el-tag v-else-if="membershipStatus === 'REJECTED'" type="danger">
              Demande refusée
            </el-tag>
          </div>

          <div v-else class="chat-wrapper">
            <el-scrollbar ref="publicScrollbar" class="messages-scroll">
              <div
                v-for="msg in publicMessages"
                :key="msg.id"
                :class="['message-row', { own: msg.userId === userId }]"
              >
                <div class="message-bubble">
                  <div class="message-author"><UserLink :username="msg.user?.username" /></div>
                  <div class="message-content">{{ msg.content }}</div>
                  <div class="message-time">{{ formatDate(msg.createdAt) }}</div>
                </div>
                <el-button
                  v-if="msg.userId === userId || isModeratorOrAdmin"
                  type="danger"
                  link
                  size="small"
                  @click="deleteMessage(msg.id, 'public')"
                >
                  <el-icon><Delete /></el-icon>
                </el-button>
              </div>
            </el-scrollbar>
            <div class="chat-input-row">
              <!-- Entrée va à la ligne ; envoi par le bouton ou Ctrl+Entrée (⌘+Entrée sur Mac) -->
              <el-input
                v-model="newPublicMessage"
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 5 }"
                placeholder="Écrire un message... (Ctrl+Entrée pour envoyer)"
                :maxlength="maxMessageLength"
                show-word-limit
                @keydown.enter.ctrl.prevent="sendPublicMessage"
                @keydown.enter.meta.prevent="sendPublicMessage"
              />
              <el-button type="primary" class="send-btn" @click="sendPublicMessage">
                <el-icon><Promotion /></el-icon>
              </el-button>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Sous-canaux privés" name="private">
          <div v-if="ge.privateChannels?.length === 0" class="empty">
            <el-empty description="Aucun sous-canal privé pour le moment." />
          </div>

          <div class="channel-list">
            <el-card
              v-for="channel in ge.privateChannels"
              :key="channel.id"
              :class="['channel-card', { active: selectedChannel?.id === channel.id }]"
              shadow="hover"
              @click="selectChannel(channel)"
            >
              <div class="channel-header">
                <strong>{{ channel.name }}</strong>
                <el-tag size="small" type="info">{{ channel.neighborhood }}</el-tag>
              </div>
              <p class="channel-desc">{{ channel.description }}</p>
              <el-tag
                v-if="myChannelMemberships[channel.id] === 'APPROVED'"
                type="success"
                size="small"
              >
                Membre
              </el-tag>
              <el-tag
                v-else-if="myChannelMemberships[channel.id] === 'PENDING'"
                type="warning"
                size="small"
              >
                En attente
              </el-tag>
              <el-tag v-else type="info" size="small">Non membre</el-tag>
            </el-card>
          </div>

          <div v-if="selectedChannel" class="chat-wrapper">
            <h3>{{ selectedChannel.name }} — {{ selectedChannel.neighborhood }}</h3>

            <div v-if="!isChannelMember(selectedChannel.id)" class="not-member">
              <el-alert
                title="Vous devez être membre de ce sous-canal pour participer."
                type="warning"
                show-icon
                :closable="false"
              />
              <el-button
                v-if="!getChannelMembershipStatus(selectedChannel.id)"
                type="primary"
                class="join-btn"
                @click="requestChannelMembership(selectedChannel.id)"
              >
                Demander à rejoindre
              </el-button>
              <el-tag v-else-if="getChannelMembershipStatus(selectedChannel.id) === 'PENDING'" type="warning">
                Demande en attente
              </el-tag>
            </div>

            <div v-else class="chat-body">
              <el-scrollbar ref="privateScrollbar" class="messages-scroll">
                <div
                  v-for="msg in privateMessages"
                  :key="msg.id"
                  :class="['message-row', { own: msg.userId === userId }]"
                >
                  <div class="message-bubble">
                    <div class="message-author"><UserLink :username="msg.user?.username" /></div>
                    <div class="message-content">{{ msg.content }}</div>
                    <div class="message-time">{{ formatDate(msg.createdAt) }}</div>
                  </div>
                  <el-button
                    v-if="msg.userId === userId || isModeratorOrAdmin"
                    type="danger"
                    link
                    size="small"
                    @click="deleteMessage(msg.id, 'private')"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
              </el-scrollbar>
              <div class="chat-input-row">
                <el-input
                  v-model="newPrivateMessage"
                  type="textarea"
                  :autosize="{ minRows: 1, maxRows: 5 }"
                  placeholder="Écrire un message... (Ctrl+Entrée pour envoyer)"
                  :maxlength="maxMessageLength"
                  show-word-limit
                  @keydown.enter.ctrl.prevent="sendPrivateMessage"
                  @keydown.enter.meta.prevent="sendPrivateMessage"
                />
                <el-button type="primary" class="send-btn" @click="sendPrivateMessage">
                  <el-icon><Promotion /></el-icon>
                </el-button>
              </div>
              <!-- Bouton discret, placé sous la discussion -->
              <div class="leave-row">
                <el-button type="danger" link size="small" @click="leaveChannel(selectedChannel.id)">
                  Quitter le sous-canal
                </el-button>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Ressources" name="resources">
          <div v-if="!isMember" class="not-member">
            <el-alert
              title="Vous devez être membre approuvé de ce groupe pour voir et partager des ressources."
              type="warning"
              show-icon
              :closable="false"
            />
          </div>

          <div v-else>
            <el-button type="primary" @click="showCreateResource = !showCreateResource">
              {{ showCreateResource ? 'Annuler' : 'Partager une ressource' }}
            </el-button>

            <div v-if="showCreateResource" class="edit-form-section">
              <el-form label-position="top" @submit.prevent>
                <el-form-item label="Titre">
                  <el-input v-model="newResource.title" placeholder="Titre de la ressource" maxlength="150" />
                </el-form-item>
                <el-form-item label="Lien">
                  <el-input v-model="newResource.url" placeholder="https://..." maxlength="500" />
                </el-form-item>
                <el-form-item label="Thème">
                  <!-- Saisie libre (conservée même sans valider), thèmes existants proposés en suggestion -->
                  <el-autocomplete
                    v-model="newResource.theme"
                    :fetch-suggestions="suggestThemes"
                    placeholder="Saisir un thème ou en choisir un existant"
                    maxlength="80"
                    clearable
                    style="width: 100%;"
                  />
                </el-form-item>
                <el-form-item label="Description (facultative)">
                  <el-input
                    v-model="newResource.description"
                    type="textarea"
                    :rows="2"
                    maxlength="1000"
                    show-word-limit
                    placeholder="Description"
                  />
                </el-form-item>
                <el-form-item>
                  <el-button type="success" @click="createResource">Partager</el-button>
                </el-form-item>
              </el-form>
            </div>

            <el-empty v-if="resources.length === 0" description="Aucune ressource partagée pour le moment." />

            <div v-for="group in resourcesByTheme" :key="group.theme" class="resource-theme">
              <h3>{{ group.theme }}</h3>
              <el-card v-for="resource in group.items" :key="resource.id" class="resource-card" shadow="hover">
                <div class="resource-header">
                  <a :href="resource.url" target="_blank" rel="noopener noreferrer" class="resource-link">
                    {{ resource.title }}
                  </a>
                  <el-button
                    v-if="resource.userId === userId || isModeratorOrAdmin"
                    type="danger"
                    link
                    size="small"
                    @click="deleteResource(resource.id)"
                  >
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
                <p v-if="resource.description" class="resource-desc">{{ resource.description }}</p>
                <div class="message-time">
                  Partagé par <UserLink :username="resource.user?.username" /> · {{ formatDate(resource.createdAt) }}
                </div>
              </el-card>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane v-if="isModeratorOrAdmin" label="Gestion" name="manage">
          <el-collapse v-model="manageCollapse">
            <el-collapse-item title="Modifier le groupe" name="edit">
              <el-button type="primary" @click="toggleEditGE">
                {{ showEditGE ? 'Annuler' : 'Modifier le groupe' }}
              </el-button>
              <div v-if="showEditGE" class="edit-form-section">
                <el-form label-position="top" @submit.prevent>
                  <el-form-item label="Nom">
                    <el-input v-model="editGE.name" placeholder="Nom du groupe" />
                  </el-form-item>
                  <el-form-item label="Description">
                    <el-input v-model="editGE.description" type="textarea" :rows="3" placeholder="Description" />
                  </el-form-item>
                  <el-form-item label="Thématique">
                    <el-select v-model="editGE.interestId" placeholder="Choisir une thématique" style="width: 100%;">
                      <el-option
                        v-for="interest in interests"
                        :key="interest.id"
                        :label="interest.name"
                        :value="interest.id"
                      />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="Modérateurs">
                    <el-checkbox-group v-model="editGE.moderatorIds">
                      <el-checkbox
                        v-for="mod in moderatorUsers"
                        :key="mod.id"
                        :label="mod.id"
                      >
                        {{ mod.username }} ({{ mod.status }})
                      </el-checkbox>
                    </el-checkbox-group>
                  </el-form-item>
                  <el-form-item>
                    <el-button type="success" @click="updateGE">Enregistrer</el-button>
                  </el-form-item>
                </el-form>
              </div>
            </el-collapse-item>

            <el-collapse-item title="Demandes d'adhésion au groupe" name="requests">
              <el-empty v-if="pendingMemberships.length === 0" description="Aucune demande en attente" />
              <el-table v-else :data="pendingMemberships" style="width: 100%;">
                <el-table-column label="Utilisateur">
                  <template #default="scope">
                    <UserLink :username="scope.row.user && scope.row.user.username" />
                  </template>
                </el-table-column>
                <el-table-column label="Actions" width="180">
                  <template #default="scope">
                    <el-button type="success" size="small" @click="approveMembership(scope.row.id)">
                      Approuver
                    </el-button>
                    <el-button type="danger" size="small" @click="rejectMembership(scope.row.id)">
                      Refuser
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>

            <el-collapse-item title="Modérateurs" name="roles">
              <p class="roles-hint">Passez un membre du groupe en modérateur, ou retirez-lui ce rôle.</p>
              <el-empty v-if="roleCandidates.length === 0" description="Aucun autre membre dans le groupe" />
              <el-table v-else :data="roleCandidates" style="width: 100%;">
                <el-table-column label="Membre">
                  <template #default="scope">
                    <UserLink :username="scope.row.user.username" />
                  </template>
                </el-table-column>
                <el-table-column label="Rôle" width="140">
                  <template #default="scope">
                    <el-tag :type="roleTagType(scope.row.user.status)" size="small">
                      {{ roleLabel(scope.row.user.status) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Actions" width="180">
                  <template #default="scope">
                    <el-button
                      v-if="scope.row.user.status === 'DEFAULT'"
                      type="success"
                      size="small"
                      @click="setMemberRole(scope.row.user, 'MODERATOR')"
                    >
                      Passer modérateur
                    </el-button>
                    <el-button
                      v-else-if="scope.row.user.status === 'MODERATOR'"
                      type="warning"
                      size="small"
                      @click="setMemberRole(scope.row.user, 'DEFAULT')"
                    >
                      Retirer modérateur
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>

            <el-collapse-item title="Sous-canaux privés" name="channels">
              <el-button type="primary" @click="showCreateChannel = !showCreateChannel">
                {{ showCreateChannel ? 'Annuler' : 'Créer un sous-canal' }}
              </el-button>

              <div v-if="showCreateChannel" class="edit-form-section">
                <el-form label-position="top" @submit.prevent>
                  <el-form-item label="Nom">
                    <el-input v-model="newChannel.name" placeholder="Nom du canal" />
                  </el-form-item>
                  <el-form-item label="Quartier">
                    <el-input v-model="newChannel.neighborhood" placeholder="Quartier" />
                  </el-form-item>
                  <el-form-item label="Description">
                    <el-input v-model="newChannel.description" type="textarea" :rows="2" placeholder="Description" />
                  </el-form-item>
                  <el-form-item>
                    <el-button type="success" @click="createChannel">Créer</el-button>
                  </el-form-item>
                </el-form>
              </div>

              <el-table :data="ge.privateChannels" style="width: 100%; margin-top: 16px;">
                <el-table-column prop="name" label="Nom" />
                <el-table-column prop="neighborhood" label="Quartier" />
                <el-table-column label="Actions" width="360">
                  <template #default="scope">
                    <el-button
                      size="small"
                      @click="showChannelRequests[scope.row.id] = !showChannelRequests[scope.row.id]; fetchChannelRequests(scope.row.id)"
                    >
                      {{ showChannelRequests[scope.row.id] ? 'Masquer' : 'Voir les demandes' }}
                    </el-button>
                    <el-button size="small" @click="openEditChannel(scope.row)">
                      Modifier
                    </el-button>
                    <el-button type="danger" size="small" @click="deleteChannel(scope.row.id)">
                      Supprimer
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>

              <div v-for="channel in ge.privateChannels" :key="'req-' + channel.id">
                <div v-if="showChannelRequests[channel.id]" class="channel-requests">
                  <h4>Demandes pour {{ channel.name }}</h4>
                  <el-empty v-if="!channelRequests[channel.id] || channelRequests[channel.id].length === 0" description="Aucune demande en attente" />
                  <el-table v-else :data="channelRequests[channel.id]" style="width: 100%;">
                    <el-table-column label="Utilisateur">
                      <template #default="scope">
                        <UserLink :username="scope.row.user && scope.row.user.username" />
                      </template>
                    </el-table-column>
                    <el-table-column label="Actions" width="180">
                      <template #default="scope">
                        <el-button type="success" size="small" @click="approveChannelRequest(channel.id, scope.row.id)">
                          Approuver
                        </el-button>
                        <el-button type="danger" size="small" @click="rejectChannelRequest(channel.id, scope.row.id)">
                          Refuser
                        </el-button>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </el-collapse-item>

            <el-collapse-item title="Supprimer le groupe" name="danger">
              <p class="danger-text">
                La suppression est définitive : messages, ressources, membres et sous-canaux seront effacés.
              </p>
              <el-button type="danger" class="delete-ge-btn" @click="deleteGE">Supprimer le groupe</el-button>
            </el-collapse-item>
          </el-collapse>

          <el-dialog v-model="editChannelVisible" title="Modifier le sous-canal" width="min(500px, 92vw)">
            <el-form label-position="top" @submit.prevent>
              <el-form-item label="Nom">
                <el-input v-model="editChannel.name" maxlength="100" />
              </el-form-item>
              <el-form-item label="Quartier">
                <el-input v-model="editChannel.neighborhood" maxlength="100" />
              </el-form-item>
              <el-form-item label="Description">
                <el-input v-model="editChannel.description" type="textarea" :rows="3" />
              </el-form-item>
            </el-form>
            <template #footer>
              <el-button @click="editChannelVisible = false">Annuler</el-button>
              <el-button type="primary" class="save-channel-btn" @click="updateChannel">Enregistrer</el-button>
            </template>
          </el-dialog>
        </el-tab-pane>
      </el-tabs>

      <!-- Bouton discret en bas de page -->
      <div v-if="isMember" class="leave-row">
        <el-button type="danger" link size="small" @click="leaveGE">
          Quitter le groupe
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import UserLink from '@/components/UserLink.vue'
import api from '@/api';
import { notifySuccess, notifyWarning, notifyError, confirmAction } from '@/notify'
import { Delete, Promotion } from '@element-plus/icons-vue'

export default {
  name: 'GroupeEntraideDetail',
  components: { Navbar, UserLink, Delete, Promotion },

  props: ['geId'],

  data() {
    return {
      ge: {},
      userId: null,
      userStatus: null,
      isLoggedIn: false,
      loading: true,
      activeTab: 'public',
      publicMessages: [],
      privateMessages: [],
      newPublicMessage: '',
      newPrivateMessage: '',
      selectedChannel: null,
      showCreateChannel: false,
      newChannel: { name: '', description: '', neighborhood: '' },
      editChannelVisible: false,
      editChannel: { id: null, name: '', description: '', neighborhood: '' },
      myChannelMemberships: {},
      channelMembershipLoaded: {},
      channelRequests: {},
      showChannelRequests: {},
      showEditGE: false,
      editGE: { name: '', description: '', interestId: '', moderatorIds: [] },
      moderatorUsers: [],
      interests: [],
      manageCollapse: ['edit'],
      pollInterval: null,
      // Même limite que le backend (createGeMessage / createPrivateChannelMessage)
      maxMessageLength: 2000,
      resources: [],
      showCreateResource: false,
      newResource: { title: '', url: '', theme: '', description: '' },
    }
  },

  computed: {
    isMember() {
      if (!this.ge.members) return false
      return this.ge.members.some((m) => m.userId === this.userId && m.status === 'APPROVED')
    },
    membershipStatus() {
      if (!this.ge.members) return null
      const m = this.ge.members.find((m) => m.userId === this.userId)
      return m ? m.status : null
    },
    isModeratorOrAdmin() {
      if (!this.isLoggedIn) return false
      if (this.userStatus === 'ADMIN') return true
      if (this.userStatus === 'MODERATOR' && this.ge.moderators) {
        return this.ge.moderators.some((m) => m.id === this.userId)
      }
      return false
    },
    pendingMemberships() {
      if (!this.ge.members) return []
      return this.ge.members.filter((m) => m.status === 'PENDING')
    },
    // Membres approuvés (hors soi-même) dont on peut changer le rôle depuis l'onglet Gestion
    roleCandidates() {
      if (!this.ge.members) return []
      return this.ge.members.filter((m) => m.status === 'APPROVED' && m.user && m.userId !== this.userId)
    },
    // Thèmes déjà utilisés, proposés dans le formulaire
    resourceThemes() {
      return [...new Set(this.resources.map((r) => r.theme))].sort((a, b) => a.localeCompare(b))
    },
    // Ressources regroupées par thème (ordre alphabétique des thèmes)
    resourcesByTheme() {
      return this.resourceThemes.map((theme) => ({
        theme,
        items: this.resources.filter((r) => r.theme === theme),
      }))
    },
  },

  mounted() {
    this.checkSession().then(() => {
      this.fetchGE()
    })
    this.pollInterval = setInterval(this.pollMessages, 5000)
  },

  beforeUnmount() {
    clearInterval(this.pollInterval)
  },

  methods: {
    pollMessages() {
      if (this.activeTab === 'public' && this.isMember) {
        this.fetchPublicMessages(true)
      } else if (this.activeTab === 'private' && this.selectedChannel && this.isChannelMember(this.selectedChannel.id)) {
        // fetchPrivateMessages relance l'erreur : on l'absorbe ici pour éviter un rejet non géré toutes les 5 s
        this.fetchPrivateMessages(this.selectedChannel.id, true).catch(() => {})
      }
    },

    isNearBottom(refName) {
      const scrollbar = this.$refs[refName]
      if (!scrollbar || !scrollbar.wrapRef) return true
      const { scrollTop, scrollHeight, clientHeight } = scrollbar.wrapRef
      return scrollHeight - (scrollTop + clientHeight) < 60
    },

    async checkSession() {
      try {
        const res = await api.get('/session/me', { withCredentials: true })
        if (res.status === 200) {
          this.isLoggedIn = true
          this.userId = res.data.id
          this.userStatus = res.data.status
        }
      } catch (err) {
        this.isLoggedIn = false
      }
    },

    async fetchGE() {
      try {
        const res = await api.get(`/ge/${this.geId}`, { withCredentials: true })
        this.ge = res.data.ge || {}
        if (this.ge.privateChannels) {
          this.ge.privateChannels.forEach((channel) => {
            if (channel.members && channel.members.length > 0) {
              this.myChannelMemberships[channel.id] = channel.members[0].status
            } else {
              this.myChannelMemberships[channel.id] = 'NOT_MEMBER'
            }
            this.channelMembershipLoaded[channel.id] = true
          })
        }
        if (this.isMember) {
          this.fetchPublicMessages()
          this.fetchResources()
        }
      } catch (err) {
        console.error('Error fetching GE:', err)
      } finally {
        this.loading = false
      }
    },

    async fetchPublicMessages(silent = false) {
      try {
        const wasNearBottom = this.isNearBottom('publicScrollbar')
        const res = await api.get(`/ge/${this.geId}/messages`, { withCredentials: true })
        this.publicMessages = res.data.messages || []
        if (!silent || wasNearBottom) {
          this.$nextTick(() => {
            this.scrollToBottom('publicScrollbar')
          })
        }
      } catch (err) {
        console.error('Error fetching public messages:', err)
      }
    },

    async sendPublicMessage() {
      if (!this.newPublicMessage.trim()) return
      try {
        await api.post(
          `/ge/${this.geId}/messages`,
          { content: this.newPublicMessage },
          { withCredentials: true }
        )
        this.newPublicMessage = ''
        this.fetchPublicMessages()
      } catch (err) {
        notifyError(err, "Erreur d'envoi")
      }
    },

    async fetchResources() {
      try {
        const res = await api.get(`/ge/${this.geId}/resources`)
        this.resources = res.data.resources || []
      } catch (err) {
        console.error('Error fetching resources:', err)
      }
    },

    // Suggestions de l'autocomplete : thèmes existants contenant la saisie (insensible à la casse)
    suggestThemes(query, cb) {
      const search = (query || '').trim().toLowerCase()
      cb(
        this.resourceThemes
          .filter((theme) => theme.toLowerCase().includes(search))
          .map((theme) => ({ value: theme }))
      )
    },

    async createResource() {
      const { title, url, theme } = this.newResource
      if (!title.trim() || !url.trim() || !theme.trim()) {
        notifyWarning('Le titre, le lien et le thème sont obligatoires.')
        return
      }
      try {
        await api.post(`/ge/${this.geId}/resources`, this.newResource)
        this.newResource = { title: '', url: '', theme: '', description: '' }
        this.showCreateResource = false
        notifySuccess('Ressource partagée !')
        this.fetchResources()
      } catch (err) {
        notifyError(err, 'Erreur lors du partage')
      }
    },

    async deleteResource(resourceId) {
      if (!(await confirmAction('Supprimer cette ressource ?'))) return
      try {
        await api.delete(`/ge/${this.geId}/resources/${resourceId}`)
        this.fetchResources()
      } catch (err) {
        notifyError(err, 'Erreur de suppression')
      }
    },

    async selectChannel(channel) {
      this.selectedChannel = channel
      this.channelMembershipLoaded[channel.id] = false
      try {
        await this.fetchPrivateMessages(channel.id)
        this.myChannelMemberships[channel.id] = 'APPROVED'
        this.channelMembershipLoaded[channel.id] = true
      } catch (err) {
        // Un 403 signifie « pas encore membre » : on ne doit pas écraser une demande déjà en attente
        if (err.response && err.response.status === 403) {
          if (this.myChannelMemberships[channel.id] !== 'PENDING') {
            this.myChannelMemberships[channel.id] = 'NOT_MEMBER'
          }
          this.channelMembershipLoaded[channel.id] = false
        }
      }
    },

    async fetchPrivateMessages(channelId, silent = false) {
      try {
        const wasNearBottom = this.isNearBottom('privateScrollbar')
        const res = await api.get(
          `/ge/${this.geId}/private-channels/${channelId}/messages`,
          { withCredentials: true }
        )
        this.privateMessages = res.data.messages || []
        if (!silent || wasNearBottom) {
          this.$nextTick(() => {
            this.scrollToBottom('privateScrollbar')
          })
        }
      } catch (err) {
        console.error('Error fetching private messages:', err)
        throw err
      }
    },

    async sendPrivateMessage() {
      if (!this.newPrivateMessage.trim() || !this.selectedChannel) return
      try {
        await api.post(
          `/ge/${this.geId}/private-channels/${this.selectedChannel.id}/messages`,
          { content: this.newPrivateMessage },
          { withCredentials: true }
        )
        this.newPrivateMessage = ''
        this.fetchPrivateMessages(this.selectedChannel.id).catch(() => {})
      } catch (err) {
        notifyError(err, "Erreur d'envoi")
      }
    },

    async deleteMessage(messageId, type) {
      if (!(await confirmAction('Supprimer ce message ?'))) return
      try {
        let url
        if (type === 'public') {
          url = `/ge/${this.geId}/messages/${messageId}`
        } else {
          url = `/ge/${this.geId}/private-channels/${this.selectedChannel.id}/messages/${messageId}`
        }
        await api.delete(url, { withCredentials: true })
        if (type === 'public') this.fetchPublicMessages()
        else this.fetchPrivateMessages(this.selectedChannel.id).catch(() => {})
      } catch (err) {
        notifyError(err, 'Erreur de suppression')
      }
    },

    async requestMembership() {
      try {
        await api.post(
          `/ge/${this.geId}/membership-request`,
          {},
          { withCredentials: true }
        )
        notifySuccess('Demande envoyée !')
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async requestChannelMembership(channelId) {
      try {
        await api.post(
          `/ge/${this.geId}/private-channels/${channelId}/membership-request`,
          {},
          { withCredentials: true }
        )
        notifySuccess('Demande envoyée !')
        this.myChannelMemberships[channelId] = 'PENDING'
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    isChannelMember(channelId) {
      if (this.myChannelMemberships[channelId] === 'APPROVED') return true
      return false
    },

    getChannelMembershipStatus(channelId) {
      const status = this.myChannelMemberships[channelId]
      if (status === 'NOT_MEMBER') return null
      return status || null
    },

    async fetchChannelRequests(channelId) {
      try {
        const res = await api.get(
          `/ge/${this.geId}/private-channels/${channelId}/membership-requests`,
          { withCredentials: true }
        )
        this.channelRequests[channelId] = res.data.requests || []
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async approveChannelRequest(channelId, requestId) {
      try {
        await api.patch(
          `/ge/${this.geId}/private-channels/${channelId}/membership-requests/${requestId}/approve`,
          {},
          { withCredentials: true }
        )
        this.fetchChannelRequests(channelId)
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async rejectChannelRequest(channelId, requestId) {
      try {
        await api.patch(
          `/ge/${this.geId}/private-channels/${channelId}/membership-requests/${requestId}/reject`,
          {},
          { withCredentials: true }
        )
        this.fetchChannelRequests(channelId)
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async fetchModerators() {
      try {
        const res = await api.get('/search/moderators', { withCredentials: true })
        this.moderatorUsers = res.data.users || []
      } catch (err) {
        console.error('Error fetching moderators:', err)
      }
    },

    toggleEditGE() {
      this.showEditGE = !this.showEditGE
      if (this.showEditGE) {
        this.editGE.name = this.ge.name || ''
        this.editGE.description = this.ge.description || ''
        this.editGE.interestId = this.ge.interest?.id || ''
        this.editGE.moderatorIds = this.ge.moderators ? this.ge.moderators.map((m) => m.id) : []
        this.fetchInterests()
        this.fetchModerators()
      }
    },

    async fetchInterests() {
      try {
        const res = await api.get('/interest/getAllInterest', { withCredentials: true })
        this.interests = res.data.allInterests || []
      } catch (err) {
        console.error('Error fetching interests:', err)
      }
    },

    async updateGE() {
      try {
        const payload = {
          name: this.editGE.name,
          description: this.editGE.description,
          interestId: this.editGE.interestId,
          moderatorIds: this.editGE.moderatorIds,
        }
        await api.patch(`/ge/${this.geId}`, payload, { withCredentials: true })
        notifySuccess('Groupe modifié !')
        this.showEditGE = false
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async approveMembership(membershipId) {
      try {
        await api.patch(
          `/ge/${this.geId}/membership-requests/${membershipId}/approve`,
          {},
          { withCredentials: true }
        )
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async rejectMembership(membershipId) {
      try {
        await api.patch(
          `/ge/${this.geId}/membership-requests/${membershipId}/reject`,
          {},
          { withCredentials: true }
        )
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    roleLabel(status) {
      if (status === 'ADMIN') return 'Administrateur'
      if (status === 'MODERATOR') return 'Modérateur'
      return 'Membre'
    },

    roleTagType(status) {
      if (status === 'ADMIN') return 'danger'
      if (status === 'MODERATOR') return 'success'
      return 'info'
    },

    // Passe un membre du groupe en MODERATOR ou le rétrograde en DEFAULT
    // (le backend vérifie que les deux utilisateurs partagent ce groupe d'entraide)
    async setMemberRole(user, status) {
      const message = status === 'MODERATOR'
        ? `Passer ${user.username} en modérateur ?`
        : `Retirer le rôle de modérateur à ${user.username} ?`
      if (!(await confirmAction(message))) return
      try {
        await api.patch(`/user/${user.id}/status`, { status }, { withCredentials: true })
        notifySuccess(status === 'MODERATOR' ? `${user.username} est maintenant modérateur` : `${user.username} n'est plus modérateur`)
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur lors du changement de rôle')
      }
    },

    async leaveGE() {
      if (!(await confirmAction("Voulez-vous vraiment quitter ce groupe d'entraide ?"))) return
      try {
        await api.delete(`/ge/${this.geId}/membership`, { withCredentials: true })
        notifySuccess('Vous avez quitté le groupe.')
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async leaveChannel(channelId) {
      if (!(await confirmAction('Voulez-vous vraiment quitter ce sous-canal ?'))) return
      try {
        await api.delete(
          `/ge/${this.geId}/private-channels/${channelId}/membership`,
          { withCredentials: true }
        )
        notifySuccess('Vous avez quitté le sous-canal.')
        this.myChannelMemberships[channelId] = 'NOT_MEMBER'
        this.selectedChannel = null
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async createChannel() {
      try {
        await api.post(
          `/ge/${this.geId}/private-channels`,
          this.newChannel,
          { withCredentials: true }
        )
        notifySuccess('Sous-canal créé !')
        this.showCreateChannel = false
        this.newChannel = { name: '', description: '', neighborhood: '' }
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    async deleteChannel(channelId) {
      if (!(await confirmAction('Supprimer ce sous-canal ?'))) return
      try {
        await api.delete(
          `/ge/${this.geId}/private-channels/${channelId}`,
          { withCredentials: true }
        )
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur')
      }
    },

    openEditChannel(channel) {
      this.editChannel = {
        id: channel.id,
        name: channel.name || '',
        description: channel.description || '',
        neighborhood: channel.neighborhood || '',
      }
      this.editChannelVisible = true
    },

    async updateChannel() {
      const { id, name, description, neighborhood } = this.editChannel
      if (!name.trim() || !neighborhood.trim()) {
        notifyWarning('Le nom et le quartier sont obligatoires')
        return
      }
      try {
        await api.patch(`/ge/${this.geId}/private-channels/${id}`, { name, description, neighborhood })
        notifySuccess('Sous-canal modifié')
        this.editChannelVisible = false
        this.fetchGE()
      } catch (err) {
        notifyError(err, 'Erreur lors de la modification du sous-canal')
      }
    },

    // Suppression définitive du groupe (ADMIN ou modérateur du groupe), puis retour à la liste
    async deleteGE() {
      const confirmed = await confirmAction(
        `Supprimer définitivement le groupe « ${this.ge.name} » ?`,
        { title: 'Supprimer le groupe', confirmText: 'Supprimer' }
      )
      if (!confirmed) return
      try {
        await api.delete(`/ge/${this.geId}`)
        clearInterval(this.pollInterval)
        notifySuccess('Groupe supprimé')
        this.$router.push('/groupes-entraide')
      } catch (err) {
        notifyError(err, 'Erreur lors de la suppression du groupe')
      }
    },

    scrollToBottom(refName) {
      const scrollbar = this.$refs[refName]
      if (scrollbar && scrollbar.wrapRef) {
        scrollbar.wrapRef.scrollTop = scrollbar.wrapRef.scrollHeight
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
      })
    },
  },
}
</script>

<style scoped>
.ge-detail-page {
  background-color: rgba(21, 109, 123);
  min-height: 100vh;
  padding-top: 60px;
  color: white;
}

.ge-detail-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

.ge-info-card {
  margin-bottom: 20px;
}

.ge-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ge-info-header h1 {
  margin: 0;
  color: #303133;
}

.description {
  color: #606266;
  margin-bottom: 12px;
}

.meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.mod-tag {
  margin-left: 6px;
}

.not-member {
  text-align: center;
  padding: 40px;
}

.join-btn {
  margin-top: 16px;
}

.chat-wrapper {
  display: flex;
  flex-direction: column;
  height: 500px;
}

/* Conteneur intermédiaire du sous-canal : sans flex, la liste de messages déborde et masque la saisie */
.chat-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.messages-scroll {
  flex: 1;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.message-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.message-row.own {
  flex-direction: row-reverse;
}

.message-bubble {
  background: white;
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 70%;
  word-break: break-word;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.message-row.own .message-bubble {
  background: #ecf5ff;
}

.message-author {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
  color: #409eff;
}

.message-content {
  color: #303133;
  font-size: 14px;
  /* Conserve les retours à la ligne des messages */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.message-time {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
  text-align: right;
}

.chat-input-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.empty {
  padding: 40px;
}

.danger-text {
  color: #606266;
  margin: 0 0 12px;
}

.channel-list {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.channel-card {
  width: 240px;
  cursor: pointer;
  border: 2px solid transparent;
}

.channel-card.active {
  border-color: #409eff;
}

.channel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.channel-desc {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

/* Boutons « Quitter » discrets, en bas */
.leave-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.resource-theme {
  margin-top: 20px;
}

.resource-theme h3 {
  color: #156d7b;
  margin-bottom: 10px;
}

.resource-card {
  margin-bottom: 10px;
}

.resource-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.resource-link {
  font-weight: bold;
  color: #409eff;
  word-break: break-word;
}

.resource-desc {
  font-size: 13px;
  color: #606266;
  margin: 6px 0 0;
}

.edit-form-section {
  margin-top: 16px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.roles-hint {
  margin: 0 0 12px;
  color: #606266;
  font-size: 13px;
}

.channel-requests {
  margin-top: 16px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.channel-requests h4 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #303133;
}

.loading {
  max-width: 600px;
  margin: 100px auto;
}

:deep(.el-tabs__header) {
  margin-bottom: 0;
}

:deep(.el-tabs__content) {
  background: white;
  padding: 20px;
  border-radius: 0 0 4px 4px;
}
</style>
